import { h } from 'preact'
import { sprintf } from 'sprintf'

let _cachedCSRFToken
let _cachedCurrentUser

// These are attributes where fewer is better, e.g. turnovers, fouls, so the
// default sort is ascending. We also throw in strings here (name, etc) and the 
// jersey number
const ASCENDING_ATTRIBUTES = new Set([
  'lastname',
  'exact_position',
  'turnovers',
  'per48_turnovers',
  'fouls',
  'per48_fouls',
  'number',
  'team_abbreviations',
])

export function defaultSortDirection(attribute) {
  return ASCENDING_ATTRIBUTES.has(attribute) ? 'asc' : 'desc'
}

export function getCSRFToken() {
  if ( !_cachedCSRFToken ) {
    _cachedCSRFToken = document.querySelector("meta[name=csrf-token]").content
  }
  return _cachedCSRFToken
}

export function timeAgoInWords(timestamp) {
  // A naive assumption, but...whatever
  if ( ! timestamp ) {
    return 'just now'
  }
  
  const timeNow = new Date()
  const timeThen = new Date(timestamp)
  const secondsAgo = (timeNow - timeThen) / 1000
  if ( secondsAgo < 120 ) {
    return 'just now'
  }

  if ( secondsAgo < 3600 ) {
    return `${Math.floor( secondsAgo / 60 )} minutes ago`
  }

  const hoursAgo = Math.floor( secondsAgo / 3600 )

  if ( hoursAgo === 1 ) {
    return '1 hour ago'
  }

  if ( hoursAgo < 24 ) {
    return `${hoursAgo} hours ago`
  }

  return `${timeThen.toLocaleDateString()}`  
}


export function currentUser() {
  if ( !_cachedCurrentUser ) {
    // Store wrapped in an object so that when the cookie isn't defined, we nevertheless only 
    // do the lookup once.
    _cachedCurrentUser = {
      user: getCookieJSON('boxscoregeeks_user')
    }
  }
  return _cachedCurrentUser.user
}
 
export function getCookieValue(keyName) {
  if ( typeof(document) === 'undefined' ) {
    return
  }
  
  const allCookies = decodeURIComponent(document.cookie).split(';')
  
  const cookie = allCookies.find((entry) => {
    const [ key, value ] = entry.trim().split('=')
    return key === keyName
  })
  
  if ( cookie ) {
    return cookie.trim().split('=')[1]
  }  
}

export function getCookieJSON(keyName) {
  const cookieValue = getCookieValue(keyName)
  
  if ( cookieValue ) {
    return JSON.parse(cookieValue)
  }  
}

export function formatNumber(number, precision = 0) {
  const n = round(number, precision)

  if ( n < 0 ) {
    return sprintf(`%.${precision}f`, number).replace(/^\-0/, '-')
  }

  if ( number === 0 ) {
    return '0'    
  }

  return sprintf(`%.${precision}f`, number).replace(/^(\-)?0/, '')
}

export function round(number, precision = 0) {
  const factor = Math.pow(10, precision)
  const roundedTempNumber = Math.round(number * factor)

  return roundedTempNumber / factor
}

export function linkifyTeams(teams) {
  // todo: how does this happen? Should we log it?
  if (typeof(teams) != 'string') {
    return ''
  }

  const abbreviations = teams.split(',')
  
  if ( abbreviations.length > 2 ) {
    return '(3+)'    
  }
  
  const links = abbreviations.map((t) => {
    const url = `/teams/${t}`
    const text = t.toUpperCase()
    return <a href={ url }>{ text }</a>
  })
  
  if ( links.length === 2 ) {
    return <span>{ links[0] }, { links[1] }</span>
  }
  
  return links[0]
}

export function updateAddressBar(state) {
  // Catch in case you are unit testing, and for older browsers that don't do pushState
  if ( typeof(window) == 'undefined' || window.history === void 0 || !window.history.pushState ) {
    return
  }
  
  const cleanedUpState = Object.keys(state).reduce((params, k) => {
    if ( state[k] !== void 0 ) {
      params[k] = state[k]
    }
    return params
  }, {})

  // Note that according to spec, the second argument (title) is ignored anyway.
  window.history.pushState(cleanedUpState, 'BoxscoreGeeks', buildNewUrl(cleanedUpState))
}

export function buildNewUrl(newParams = {}) {
  const url = window.location.pathname

  return url + stringifyParams(newParams)
}

export function deserializeQueryString(queryString) {
  if ( !queryString ) {
    return {}
  }

  // N.B. Don't call decodeURIComponent before splitting, b/c decoding might unescape '&' that were escaped.
  const pairs = queryString.split('&')
  
  return pairs.reduce((params, pair) => {
    const [ key, value ] = pair.split('=').map((p) => decodeURIComponent(p))
    
    // handle array params like 'positions[]=SG&positions[]=SF'
    if ( key.match(/\[\]$/) ) {
      const keyName = key.replace(/\[\]/, '')      
      const arrayValue = params[keyName] || []

      params[keyName] = arrayValue.concat(value)
    } else {
      params[key] = value
    }

    return params
  }, {})
}

export function stringifyParams(obj) {
  const params = Object.keys(obj)
  
  if ( params.length === 0 ) {
    return ''
  }
  
  const paramPairs = params.reduce((memo, param) => {
    const value = obj[param]
    
    if ( Array.isArray(value) ) {
      const paramName = `${param}[]`
      return memo.concat(value.map((v) => `${encodeURIComponent(paramName)}=${encodeURIComponent(v)}`))
    }

    return memo.concat(`${encodeURIComponent(param)}=${encodeURIComponent(obj[param])}`)
  }, [])

  return '?' + paramPairs.join('&')
}

export function hasClass(el, className) {
  if (el.classList) {
    return el.classList.contains(className);
  } else {
    return new RegExp('(^| )' + className + '( |$)', 'gi').test(el.className); 
  }
}

export function addClasses(el, classes) {
  classes.forEach(function(c) {
    if ( el.classList ) {
      el.classList.add(c);
    } else {
      el.className += ' ' + c;
    }
  })
  
  return el;
}

// see http://youmightnotneedjquery.com/ for why the crazy regex.
export function removeClasses(el, classes) {
  classes.forEach(function(c) {
    if ( el.classList ) {
      el.classList.remove(c);
    } else {
      el.className = el.className.replace(new RegExp('(^|\\b)' + c.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }
  })
  
  return el;
}

export function popupify(selector) {
  // TODO: move the below stuff to importables
  const buttons = document.querySelectorAll(selector);
  buttons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault()
      var newWindow = window.open(button.href, '', 'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600')
      newWindow.opener = null
    })
  })
}



// WEBPACK FOOTER //
// ./app/javascript/lib/utilities.js